import { uiText } from '../../utils/i18n';
import { useMemo, useState } from 'react';
import styles from '../OntologyPage.module.css';
import { ontologyRequest } from './api';
import {
  LAYER_META,
  RELATION_META,
  type EvidenceLevel,
  type FieldType,
  type KnowledgeDocument,
  type MetricObservation,
  type OntologyEntity,
  type OntologyLayer,
  type OntologyRelation,
  type OntologyType,
  type RelationKind,
  type RelationPolarity,
} from './types';

function CloseIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
}

function PlusIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
}

function fieldTypeLabel(type: FieldType): string {
  return uiText(({ text: '文本', number: '数字', boolean: '是 / 否', date: '日期', url: '链接' } as Record<FieldType, string>)[type]);
}

const ENTITY_STATUSES = ['活跃', '草稿', '待验证', '规划中', '进行中', '已完成', '暂停', '已归档'];

function ModalFrame({ eyebrow, title, children, footer, onClose, small = false }: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
  footer: React.ReactNode;
  onClose: () => void;
  small?: boolean;
}) {
  return <div className={styles.modalBackdrop} onMouseDown={onClose}>
    <div className={`${styles.modal} ${small ? styles.modalSmall : ''}`} onMouseDown={(event) => event.stopPropagation()}>
      <div className={styles.modalHeader}>
        <div><span className={styles.eyebrow}>{eyebrow}</span><h2>{title}</h2></div>
        <button className={styles.iconButton} onClick={onClose} aria-label={uiText("关闭")}><CloseIcon /></button>
      </div>
      <div className={styles.modalBody}>{children}</div>
      <div className={styles.modalFooter}>{footer}</div>
    </div>
  </div>;
}

export function EntityModal({ token, types, documents, initial, preferredTypeId, preferredLayer, onClose, onSaved }: {
  token: string | null;
  types: OntologyType[];
  documents: KnowledgeDocument[];
  initial?: OntologyEntity;
  preferredTypeId?: string;
  preferredLayer?: OntologyLayer;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const visibleTypes = useMemo(() => preferredLayer && !initial ? types.filter((type) => type.layer === preferredLayer) : types, [types, preferredLayer, initial]);
  const defaultTypeId = initial?.typeId || preferredTypeId || visibleTypes[0]?.id || types[0]?.id || '';
  const [typeId, setTypeId] = useState(defaultTypeId);
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [status, setStatus] = useState(initial?.status || '活跃');
  const [properties, setProperties] = useState<Record<string, unknown>>(initial?.properties || {});
  const [documentId, setDocumentId] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const selectedType = types.find((type) => type.id === typeId);

  const save = async () => {
    if (!name.trim() || !typeId) return;
    setSaving(true);
    setError('');
    try {
      const payload = { typeId, name: name.trim(), description: description.trim(), status, properties };
      const result = initial
        ? await ontologyRequest<{ entity: OntologyEntity }>(token, `/api/ontology/entities/${encodeURIComponent(initial.id)}`, { method: 'PUT', body: JSON.stringify(payload) })
        : await ontologyRequest<{ entity: OntologyEntity }>(token, '/api/ontology/entities', { method: 'POST', body: JSON.stringify(payload) });
      if (documentId) {
        await ontologyRequest(token, '/api/ontology/evidence', {
          method: 'POST',
          body: JSON.stringify({ entityId: result.entity.id, documentId, excerpt: excerpt.trim() }),
        });
      }
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return <ModalFrame
    eyebrow={`${selectedType ? LAYER_META[selectedType.layer].short.toUpperCase() : 'ONTOLOGY'} OBJECT`}
    title={initial ? uiText("编辑业务对象") : `新建${selectedType?.name || uiText("对象")}`}
    onClose={onClose}
    footer={<><button className={styles.secondaryButton} onClick={onClose}>{uiText("取消")}</button><button className={styles.primaryButton} onClick={save} disabled={saving || !name.trim() || !typeId}>{saving ? uiText("保存中…") : uiText("保存对象")}</button></>}
  >
    <div className={styles.formGridTwo}>
      <label className={styles.field}><span>{uiText("对象类型 *")}</span><select value={typeId} onChange={(event) => { setTypeId(event.target.value); setProperties({}); }}>
        {(visibleTypes.length ? visibleTypes : types).map((type) => <option key={type.id} value={type.id}>{LAYER_META[type.layer].short} · {type.name}</option>)}
      </select></label>
      <label className={styles.field}><span>{uiText("状态")}</span><select value={status} onChange={(event) => setStatus(event.target.value)}>
        {ENTITY_STATUSES.map((item) => <option key={item} value={item}>{uiText(item)}</option>)}
      </select></label>
    </div>
    <label className={styles.field}><span>{uiText("名称 *")}</span><input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder={selectedType?.key === 'metric' ? uiText("例如：支付转化率") : uiText("输入清晰、唯一的业务名称")} maxLength={160} /></label>
    <label className={styles.field}><span>{uiText("说明")}</span><textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder={uiText("说明它在业务里代表什么，以及不包含什么")} rows={3} maxLength={1000} /></label>

    {selectedType && selectedType.fields.length > 0 && <div className={styles.fieldSection}>
      <div className={styles.fieldSectionTitle}>{uiText("语义属性")} <small>{selectedType.name} {uiText("· 用于 Agent 理解")}</small></div>
      <div className={styles.formGridTwo}>
        {selectedType.fields.map((field) => <label className={styles.field} key={field.id}>
          <span>{field.label}{field.required ? ' *' : ''}</span>
          {field.dataType === 'boolean' ? <select value={String(properties[field.key] ?? '')} onChange={(event) => setProperties((current) => ({ ...current, [field.key]: event.target.value }))}>
            <option value="">{uiText("未设置")}</option><option value="true">{uiText("是")}</option><option value="false">{uiText("否")}</option>
          </select> : <input
            type={field.dataType === 'date' ? 'date' : field.dataType === 'number' ? 'number' : field.dataType === 'url' ? 'url' : 'text'}
            value={String(properties[field.key] ?? '')}
            onChange={(event) => setProperties((current) => ({ ...current, [field.key]: event.target.value }))}
            placeholder={field.key === 'formula' ? uiText("例如：SUM(pay_amount)") : fieldTypeLabel(field.dataType)}
          />}
        </label>)}
      </div>
    </div>}

    <div className={styles.fieldSection}>
      <div className={styles.fieldSectionTitle}>{uiText("定义证据")} <small>{uiText("可选 · 关联共享知识库")}</small></div>
      {documents.length > 0 ? <>
        <label className={styles.field}><span>{uiText("知识库文档")}</span><select value={documentId} onChange={(event) => setDocumentId(event.target.value)}>
          <option value="">{uiText("暂不关联")}</option>{documents.map((doc) => <option key={doc.id} value={doc.id}>{doc.title}</option>)}
        </select></label>
        {documentId && <label className={styles.field}><span>{uiText("证据摘要")}</span><textarea value={excerpt} onChange={(event) => setExcerpt(event.target.value)} placeholder={uiText("记录定义、口径或事实所在位置")} rows={2} maxLength={1000} /></label>}
      </> : <p className={styles.formHint}>{uiText("共享知识库暂时没有可关联文档，保存后也可以再补证据。")}</p>}
    </div>
    {error && <div className={styles.formError}>{error}</div>}
  </ModalFrame>;
}

export function RelationModal({ token, entities, documents, preferredSourceId, preferredTargetId, preferredKind, onClose, onSaved }: {
  token: string | null;
  entities: OntologyEntity[];
  documents: KnowledgeDocument[];
  preferredSourceId?: string;
  preferredTargetId?: string;
  preferredKind?: RelationKind;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [sourceEntityId, setSourceEntityId] = useState(preferredSourceId || entities[0]?.id || '');
  const [targetEntityId, setTargetEntityId] = useState(preferredTargetId || entities.find((entity) => entity.id !== (preferredSourceId || entities[0]?.id))?.id || '');
  const [kind, setKind] = useState<RelationKind>(preferredKind || 'semantic');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [confidence, setConfidence] = useState(preferredKind === 'causal' ? 50 : 100);
  const [polarity, setPolarity] = useState<RelationPolarity>('unknown');
  const [lag, setLag] = useState('');
  const [condition, setCondition] = useState('');
  const [formula, setFormula] = useState('');
  const [evidenceLevel, setEvidenceLevel] = useState<EvidenceLevel>('declared');
  const [documentId, setDocumentId] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!sourceEntityId || !targetEntityId) return;
    setSaving(true);
    setError('');
    try {
      const result = await ontologyRequest<{ relation: OntologyRelation }>(token, '/api/ontology/relations', {
        method: 'POST',
        body: JSON.stringify({ sourceEntityId, targetEntityId, kind, label: label.trim() || undefined, description: description.trim(), confidence: confidence / 100, polarity, lag: lag.trim(), condition: condition.trim(), formula: formula.trim(), evidenceLevel }),
      });
      if (documentId) {
        await ontologyRequest(token, '/api/ontology/relation-evidence', {
          method: 'POST',
          body: JSON.stringify({ relationId: result.relation.id, documentId, excerpt: excerpt.trim() }),
        });
      }
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return <ModalFrame
    eyebrow="SEMANTIC RELATION"
    title={uiText("建立业务关系")}
    onClose={onClose}
    footer={<><button className={styles.secondaryButton} onClick={onClose}>{uiText("取消")}</button><button className={styles.primaryButton} onClick={save} disabled={saving || !sourceEntityId || !targetEntityId || sourceEntityId === targetEntityId}>{saving ? uiText("建立中…") : uiText("建立关系")}</button></>}
  >
    <div className={styles.relationTypePicker}>
      {(Object.keys(RELATION_META) as RelationKind[]).map((item) => <button key={item} type="button" className={kind === item ? styles.relationTypeActive : ''} onClick={() => { setKind(item); setConfidence(item === 'causal' ? 50 : 100); }}>
        <span style={{ background: RELATION_META[item].color }} />
        <b>{RELATION_META[item].label}</b>
        <small>{RELATION_META[item].description}</small>
      </button>)}
    </div>
    <div className={styles.formGridTwo}>
      <label className={styles.field}><span>{uiText("起点 *")}</span><select value={sourceEntityId} onChange={(event) => setSourceEntityId(event.target.value)}>{entities.map((entity) => <option key={entity.id} value={entity.id}>{entity.name}</option>)}</select></label>
      <label className={styles.field}><span>{uiText("终点 *")}</span><select value={targetEntityId} onChange={(event) => setTargetEntityId(event.target.value)}>{entities.map((entity) => <option key={entity.id} value={entity.id}>{entity.name}</option>)}</select></label>
    </div>
    <label className={styles.field}><span>{uiText("关系名称")}</span><input value={label} onChange={(event) => setLabel(event.target.value)} placeholder={`留空使用“${RELATION_META[kind].label}”`} maxLength={80} /></label>

    {(kind === 'calculation' || kind === 'lineage') && <label className={styles.field}><span>{uiText("计算 / 映射表达式")}</span><textarea value={formula} onChange={(event) => setFormula(event.target.value)} placeholder={uiText("例如：SUM(pay_amount) 或 orders.pay_amount → revenue")} rows={2} maxLength={500} /></label>}
    {(kind === 'influence' || kind === 'causal') && <div className={styles.fieldSection}>
      <div className={styles.fieldSectionTitle}>{uiText("影响语义")} <small>{uiText("避免把相关性误判为因果")}</small></div>
      <div className={styles.formGridThree}>
        <label className={styles.field}><span>{uiText("方向")}</span><select value={polarity} onChange={(event) => setPolarity(event.target.value as RelationPolarity)}><option value="unknown">{uiText("未知")}</option><option value="positive">{uiText("正向")}</option><option value="negative">{uiText("负向")}</option><option value="mixed">{uiText("视条件而定")}</option></select></label>
        <label className={styles.field}><span>{uiText("证据等级")}</span><select value={evidenceLevel} onChange={(event) => setEvidenceLevel(event.target.value as EvidenceLevel)}><option value="declared">{uiText("业务声明")}</option><option value="observed">{uiText("数据观察")}</option><option value="validated">{uiText("已验证因果")}</option></select></label>
        <label className={styles.field}><span>{uiText("置信度 ·")} {confidence}%</span><input type="range" min="0" max="100" value={confidence} onChange={(event) => setConfidence(Number(event.target.value))} /></label>
      </div>
      <div className={styles.formGridTwo}>
        <label className={styles.field}><span>{uiText("影响时滞")}</span><input value={lag} onChange={(event) => setLag(event.target.value)} placeholder={uiText("例如：同日 / 7 天后")} maxLength={80} /></label>
        <label className={styles.field}><span>{uiText("适用条件")}</span><input value={condition} onChange={(event) => setCondition(event.target.value)} placeholder={uiText("例如：库存充足时")} maxLength={300} /></label>
      </div>
    </div>}

    <label className={styles.field}><span>{uiText("关系说明")}</span><textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder={uiText("补充口径、边界或业务背景")} rows={2} maxLength={500} /></label>
    <div className={styles.fieldSection}>
      <div className={styles.fieldSectionTitle}>{uiText("关系证据")} <small>{uiText("可选")}</small></div>
      {documents.length > 0 ? <>
        <label className={styles.field}><span>{uiText("知识库文档")}</span><select value={documentId} onChange={(event) => setDocumentId(event.target.value)}><option value="">{uiText("暂不关联")}</option>{documents.map((doc) => <option key={doc.id} value={doc.id}>{doc.title}</option>)}</select></label>
        {documentId && <label className={styles.field}><span>{uiText("证据摘要")}</span><textarea value={excerpt} onChange={(event) => setExcerpt(event.target.value)} rows={2} placeholder={uiText("这份证据如何支持该关系？")} /></label>}
      </> : <p className={styles.formHint}>{uiText("知识库暂无共享文档。")}</p>}
    </div>
    {error && <div className={styles.formError}>{error}</div>}
  </ModalFrame>;
}

interface DraftField { id: string; label: string; key: string; dataType: FieldType; required: boolean }

export function TypeModal({ token, onClose, onSaved }: { token: string | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  const [color, setColor] = useState('#5a6a2a');
  const [layer, setLayer] = useState<OntologyLayer>('business');
  const [fields, setFields] = useState<DraftField[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const addField = () => setFields((current) => [...current, { id: crypto.randomUUID(), label: '', key: '', dataType: 'text', required: false }]);
  const patchField = (id: string, patch: Partial<DraftField>) => setFields((current) => current.map((field) => field.id === id ? { ...field, ...patch } : field));

  const save = async () => {
    if (!name.trim() || fields.some((field) => !field.label.trim())) return;
    setSaving(true);
    setError('');
    try {
      await ontologyRequest(token, '/api/ontology/types', { method: 'POST', body: JSON.stringify({ name: name.trim(), key: key.trim() || undefined, description: description.trim(), icon: icon.trim(), color, layer, fields: fields.map(({ id: _id, ...field }) => field) }) });
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return <ModalFrame
    eyebrow="CUSTOM BUSINESS TYPE"
    title={uiText("定义业务类型")}
    onClose={onClose}
    footer={<><button className={styles.secondaryButton} onClick={onClose}>{uiText("取消")}</button><button className={styles.primaryButton} onClick={save} disabled={saving || !name.trim() || fields.some((field) => !field.label.trim())}>{saving ? uiText("创建中…") : uiText("创建类型")}</button></>}
  >
    <label className={styles.field}><span>{uiText("所属层 *")}</span><div className={styles.layerPicker}>{(Object.keys(LAYER_META) as OntologyLayer[]).map((item) => <button key={item} type="button" className={layer === item ? styles.layerPickerActive : ''} onClick={() => setLayer(item)}><span style={{ background: LAYER_META[item].color }} /> <b>{LAYER_META[item].label}</b><small>{LAYER_META[item].description}</small></button>)}</div></label>
    <div className={styles.formGridType}>
      <label className={styles.field}><span>{uiText("名称 *")}</span><input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder={uiText("例如：合同")} maxLength={80} /></label>
      <label className={styles.field}><span>{uiText("图标")}</span><input value={icon} onChange={(event) => setIcon(event.target.value.slice(0, 2))} placeholder={name.slice(0, 1) || uiText("类")} /></label>
      <label className={styles.field}><span>{uiText("颜色")}</span><input className={styles.colorInput} type="color" value={color} onChange={(event) => setColor(event.target.value)} /></label>
    </div>
    <label className={styles.field}><span>{uiText("唯一标识")}</span><input value={key} onChange={(event) => setKey(event.target.value)} placeholder={uiText("可留空自动生成，例如 contract")} maxLength={64} /></label>
    <label className={styles.field}><span>{uiText("类型说明")}</span><textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder={uiText("什么属于这个类型，什么不属于？")} rows={2} maxLength={280} /></label>
    <div className={styles.fieldSection}>
      <div className={styles.fieldBuilderHeader}><div className={styles.fieldSectionTitle}>{uiText("属性字段")} <small>{fields.length} {uiText("个")}</small></div><button className={styles.textButton} type="button" onClick={addField}><PlusIcon /> {uiText("添加字段")}</button></div>
      {fields.length === 0 ? <p className={styles.formHint}>{uiText("属性用于统一业务含义，也会成为 Agent 的结构化上下文。")}</p> : <div className={styles.fieldBuilder}>{fields.map((field) => <div className={styles.fieldRow} key={field.id}>
        <input value={field.label} onChange={(event) => patchField(field.id, { label: event.target.value })} placeholder={uiText("字段名称")} maxLength={80} />
        <input value={field.key} onChange={(event) => patchField(field.id, { key: event.target.value })} placeholder={uiText("标识（可选）")} maxLength={64} />
        <select value={field.dataType} onChange={(event) => patchField(field.id, { dataType: event.target.value as FieldType })}>{(['text', 'number', 'boolean', 'date', 'url'] as FieldType[]).map((type) => <option key={type} value={type}>{fieldTypeLabel(type)}</option>)}</select>
        <label className={styles.requiredCheck}><input type="checkbox" checked={field.required} onChange={(event) => patchField(field.id, { required: event.target.checked })} /> {uiText("必填")}</label>
        <button className={styles.rowRemove} type="button" onClick={() => setFields((current) => current.filter((item) => item.id !== field.id))}><CloseIcon /></button>
      </div>)}</div>}
    </div>
    {error && <div className={styles.formError}>{error}</div>}
  </ModalFrame>;
}

export function ObservationModal({ token, metric, onClose, onSaved }: {
  token: string | null;
  metric: OntologyEntity;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [value, setValue] = useState('');
  const [period, setPeriod] = useState('');
  const [source, setSource] = useState('');
  const [observedAt, setObservedAt] = useState(new Date().toISOString().slice(0, 10));
  const [dimensionsText, setDimensionsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!value.trim() || !Number.isFinite(Number(value))) return;
    setSaving(true);
    setError('');
    try {
      const dimensions: Record<string, string> = {};
      dimensionsText.split(/[,，\n]/).forEach((pair) => {
        const [key, ...rest] = pair.split(/[:：=]/);
        if (key?.trim() && rest.join(':').trim()) dimensions[key.trim()] = rest.join(':').trim();
      });
      await ontologyRequest<{ observation: MetricObservation }>(token, '/api/ontology/observations', {
        method: 'POST',
        body: JSON.stringify({ metricEntityId: metric.id, value: Number(value), period: period.trim(), source: source.trim(), dimensions, observedAt: new Date(`${observedAt}T12:00:00`).getTime() }),
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return <ModalFrame
    eyebrow="METRIC OBSERVATION"
    title={`记录“${metric.name}”`}
    onClose={onClose}
    small
    footer={<><button className={styles.secondaryButton} onClick={onClose}>{uiText("取消")}</button><button className={styles.primaryButton} onClick={save} disabled={saving || !value.trim() || !Number.isFinite(Number(value))}>{saving ? uiText("记录中…") : uiText("记录数值")}</button></>}
  >
    <div className={styles.formGridTwo}>
      <label className={styles.field}><span>{uiText("指标值 *")}</span><input autoFocus type="number" step="any" value={value} onChange={(event) => setValue(event.target.value)} placeholder="0" /></label>
      <label className={styles.field}><span>{uiText("观测日期")}</span><input type="date" value={observedAt} onChange={(event) => setObservedAt(event.target.value)} /></label>
    </div>
    <label className={styles.field}><span>{uiText("统计周期")}</span><input value={period} onChange={(event) => setPeriod(event.target.value)} placeholder={uiText("例如：2026-W30 / 2026-07")} maxLength={120} /></label>
    <label className={styles.field}><span>{uiText("维度切片")}</span><input value={dimensionsText} onChange={(event) => setDimensionsText(event.target.value)} placeholder={uiText("例如：渠道=自然流量，地区=华东")} /></label>
    <label className={styles.field}><span>{uiText("数值来源")}</span><input value={source} onChange={(event) => setSource(event.target.value)} placeholder={uiText("例如：经营看板 / SQL 查询")} maxLength={240} /></label>
    <p className={styles.formHint}>{uiText("连续记录两个周期后，NovaX 会计算变化并在影响分析中沿上游寻找可能驱动。")}</p>
    {error && <div className={styles.formError}>{error}</div>}
  </ModalFrame>;
}
